import { z } from 'zod';
import { OperationIdSchema } from '../../ids';
import { HolderNameSchema } from '../../models/holder-name';
import { API_PREFIX, defineEndpoint } from '../endpoint';

/** A townsperson's id (NPC-SCHEDULE): one of the layout's folk (format only). */
export const PersonIdSchema = z
  .string()
  .regex(/^[a-z][a-z0-9_]*$/)
  .max(64);

/**
 * One thing a townsperson tells (NPC-RUMOR §2): data, never text. The client picks the catalogs'
 * `rumors.<kind>.<form>` and fills it with what is here, names already resolved by the server. A
 * detail is told only as sharp as its teller knows it (NR-4): a place exact, a direction near, a
 * region far. `side` is data for a war's tone; no catalog line reads it yet.
 */
export const RumorLineSchema = z.object({
  /** Its class (`raid`, `boss_fell`, `hunger`…): the config's `rumors.kinds`. */
  kind: z.string().min(1).max(32),
  form: z.enum(['exact', 'near', 'far']),
  /** A named place of the layout: told `exact`, or a fact's place. */
  place: z.string().optional(),
  /** Where it lies from its teller (`near`). */
  direction: z.enum(['e', 'se', 's', 'sw', 'w', 'nw', 'n', 'ne']).optional(),
  /** The region it happened in (`far`, and as the context of the rest). */
  region: z.string().optional(),
  /** A power it is about (who raided, whose front, whose people). */
  power: z.string().optional(),
  /** A living community it is about. */
  community: z.string().optional(),
  /** A creature sheet or family it is about. */
  sheet: z.string().optional(),
  family: z.string().optional(),
  /** A good it is about (a shop short of it). */
  item: z.string().optional(),
  /** Characters named (NR-2: only when public): already their display names. */
  names: z.array(z.string()).max(8).optional(),
  /** Who did it, named only when a witness saw it (NR-2, RP-3). */
  author: z.string().optional(),
  /** A role's holder fallen and its successor (CREATURE-NAMED NN-4), never translated. */
  holders: z.tuple([HolderNameSchema, HolderNameSchema]).optional(),
  /** A count, as blurred as its teller knows it (NR-4). */
  count: z.number().int().min(0).optional(),
  /** Whose side its teller is on, for a war's tone (NR-5). */
  side: z.enum(['ours', 'theirs']).optional(),
  /** How heavy what it tells of is (WORLD-PRESAGE AG-7): 1 one alone, 2 with care, 3 a group. */
  degree: z.number().int().min(1).max(3).optional(),
  /** Which of its kind's lines its teller says (a kind worded by several: the Pulse's, GO-7). */
  line: z.number().int().min(0).max(7).optional(),
  /** The circle it marks on the map (NR-6), in world tiles; none when told by its region. */
  area: z.object({ x: z.number(), y: z.number(), r: z.number() }).optional(),
});
export type RumorLine = z.infer<typeof RumorLineSchema>;

/**
 * Talks to a townsperson standing within reach (`folk_not_here` otherwise): what it tells now, the
 * same for the rest of the moment of the day. `overhear` is passing by (NR-8): a wider reach, one
 * line at most. A people that hates the character tells it less, its sworn enemies nothing
 * (`silent`, NR-7). Changes nothing of the world; what it told is kept for the journal (NR-6).
 */
export const talkToFolk = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/me/talk`,
  access: 'session',
  body: z.object({ person: PersonIdSchema, overhear: z.boolean().optional() }),
  response: z.object({
    person: PersonIdSchema,
    lines: z.array(RumorLineSchema),
    silent: z.boolean(),
  }),
});

/** What the character heard and still lives in the world (NR-6): its journal's «What I heard». */
export const getHeardRumors = defineEndpoint({
  method: 'GET',
  path: `${API_PREFIX}/me/rumors`,
  access: 'session',
  response: z.object({
    heard: z.array(z.object({ person: PersonIdSchema, at: z.number(), line: RumorLineSchema })),
  }),
});

/**
 * Asks a Wanderer where it saw the character's body (DEATH-PULSE PF-4, RN-1..5): an innkeeper by its
 * inn (`person`) or a passing merchant by its stall (`merchant`, its world event), within reach. It
 * tells, as a rumor of kind `residue` (or `residue_taken`, once a band carried it to its lair), only
 * the character's own residue and only as sharp as it heard of it, for the price of that form from
 * the treasury. `no_residue` when none lies nor was carried off; `nothing_heard`, nothing paid, when
 * its news has not reached this teller. Idempotent.
 */
export const askResidueNews = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/me/residue-news`,
  access: 'session',
  body: z
    .object({
      operationId: OperationIdSchema,
      person: PersonIdSchema.optional(),
      merchant: z.string().min(1).max(64).optional(),
    })
    .refine((body) => (body.person === undefined) !== (body.merchant === undefined), {
      message: 'a person or a merchant',
    }),
  response: z.object({ line: RumorLineSchema, price: z.number().int().nonnegative() }),
});
